// Hearing one Block. The Sample route answers 200 for text this Voice has
// spoken before and 202 for one it has to render, so this waits out the render
// and then plays; a second press on the same Block is a cache hit and costs
// nothing, which is what makes comparing two Blocks by ear practical.
//
// What a Sample speaks is the Block's text, never its id: an edit of a version
// whose source is already `edit` keeps the id and changes the words, so a map
// keyed on the id alone would replay the audio of a sentence the Author has
// since rewritten. The key is therefore the id and the text together.

import type { BlockId, ChapterId, Sample } from "@contract";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PlaybackStatus } from "../../../components/audio/AudioProvider";
import { usePlayback } from "../../../components/audio/AudioProvider";
import { sampleAudioUrl, useSample } from "./useReview";

/** How long between polls of a Sample that is still rendering. */
const POLL_MS = 1000;
/** How long to wait for one before giving up; a sample job that slow has gone wrong. */
const POLL_LIMIT_MS = 60_000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** The Block and the words it holds right now; a NUL cannot occur in either half. */
const cacheKey = (blockId: BlockId, text: string) => `${blockId}\u0000${text}`;

export interface BlockSample {
  /** Asks for (or replays) the Sample of one Block; a second call while one is in flight is ignored. */
  readonly play: (blockId: BlockId, text: string) => Promise<void>;
  readonly statusFor: (blockId: BlockId | undefined, text: string | undefined) => PlaybackStatus;
}

export function useBlockSample(bookId: string, chapterId: string | undefined): BlockSample {
  const { request, poll } = useSample(bookId);
  const playback = usePlayback();
  // The whole playback context, read through a ref rather than closed over: it
  // carries `positionMs`, so a callback depending on it would be a new
  // function on every `timeupdate` and would defeat the alignment's memo for
  // the whole length of a Sample.
  const playbackRef = useRef(playback);
  playbackRef.current = playback;

  /** Which Block the request in flight belongs to, and which one last failed. */
  const [pendingFor, setPendingFor] = useState<BlockId | null>(null);
  const [failedFor, setFailedFor] = useState<BlockId | null>(null);
  /** Which Sample each Block's current text already has, so the second press skips the round trip. */
  const known = useRef(new Map<string, string>());
  /** State would be a render behind: two presses in one frame both have to see the first one. */
  const inFlight = useRef(false);
  /**
   * Bumped whenever this hook stops speaking for the Chapter it started on. A
   * Sample render runs for up to a minute, so without it a press, a Chapter
   * change, and the poll finishing would start audio under a Chapter the
   * Author has already left.
   */
  const generation = useRef(0);

  // A Chapter change ends the generation during the render that shows the new
  // Chapter rather than in an effect a paint later, because a poll resolving in
  // between would still be holding the old generation and would pass the check.
  const lastChapterId = useRef(chapterId);
  if (lastChapterId.current !== chapterId) {
    lastChapterId.current = chapterId;
    generation.current += 1;
    inFlight.current = false;
    if (pendingFor !== null) setPendingFor(null);
    if (failedFor !== null) setFailedFor(null);
  }

  // Unmounting ends it too: the AudioProvider goes with the screen, so a poll
  // that resolves afterwards would call play on a context that is gone.
  useEffect(
    () => () => {
      generation.current += 1;
      inFlight.current = false;
    },
    [],
  );

  const mutateAsync = request.mutateAsync;
  const play = useCallback(
    async (blockId: BlockId, text: string) => {
      if (!chapterId) return;
      const key = cacheKey(blockId, text);
      const cached = known.current.get(key);
      const playing = playbackRef.current;
      if (cached && playing.activeId === cached && playing.status === "playing") {
        playing.pause();
        return;
      }
      // A Sample is a paid ElevenLabs render unless the server can answer it
      // from its cache, so a double press must not buy two of them.
      if (inFlight.current) return;
      inFlight.current = true;
      const mine = generation.current;
      const mineStill = () => generation.current === mine;
      setFailedFor(null);
      setPendingFor(blockId);
      try {
        let sample: Sample = cached
          ? await poll(cached)
          : await mutateAsync({
              source: { kind: "block", chapterId: chapterId as ChapterId, blockId },
              draftText: text,
            });
        const deadline = Date.now() + POLL_LIMIT_MS;
        while (sample.status === "rendering" && Date.now() < deadline) {
          await wait(POLL_MS);
          if (!mineStill()) return;
          sample = await poll(sample.id);
        }
        if (!mineStill()) return;
        if (sample.status !== "cached") {
          setFailedFor(blockId);
          return;
        }
        known.current.set(key, sample.id);
        playbackRef.current.play(sample.id, sampleAudioUrl(sample.id));
      } catch {
        if (mineStill()) setFailedFor(blockId);
      } finally {
        if (mineStill()) {
          inFlight.current = false;
          setPendingFor(null);
        }
      }
    },
    [chapterId, mutateAsync, poll],
  );

  const statusFor = useCallback(
    (blockId: BlockId | undefined, text: string | undefined): PlaybackStatus => {
      if (!blockId) return "idle";
      if (failedFor === blockId) return "failed";
      if (pendingFor === blockId) return "loading";
      const id = known.current.get(cacheKey(blockId, text ?? ""));
      return id && playback.activeId === id ? playback.status : "idle";
    },
    [failedFor, pendingFor, playback.activeId, playback.status],
  );

  // One object per pair of stable callbacks: the screen hands `play` down into
  // the memoized alignment, and a fresh literal here would re-render every
  // mounted row on every render of the screen above it.
  return useMemo(() => ({ play, statusFor }), [play, statusFor]);
}
